import { LOGIN_TIMEOUT_MS } from "@/utils/loginRequest";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";




export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.NEXT_PUBLIC_BASE_URL}/users/`,
    prepareHeaders: (headers, { getState }) => {
      const cookies = document.cookie.split(";");
      let jwtCookie = null;

      // `.trim().startsWith("token=")` pins to the exact cookie name —
      // `.includes("token=")` would also match `refresh_token=`,
      // `g_csrf_token=`, `xsrf_token=`, etc. and overwrite the real JWT
      // when one of those sits after `token=` in document.cookie.
      cookies.map((cookie) => {
        const c = cookie.trim();
        if (c.startsWith("token=")) {
          jwtCookie = "Bearer " + c.slice("token=".length);
        }
        return null;
      });

      if (jwtCookie) {
        headers.set("authorization", jwtCookie);
      }

      return headers;
    },
  }),
  tagTypes: ['RepFavourites', 'SearchHistory'],
  endpoints: (builder) => ({

    userSignup: builder.mutation({
      query: (body) => ({
        url: `signup`,
        method: "POST",
        body: body
      }),
    }),
    userSignin: builder.mutation({
      query: (body) => ({
        url: `signin`,
        method: "POST",
        timeout: LOGIN_TIMEOUT_MS,
        body: body
      }),
    }),
    setPassword: builder.mutation({
      query: (body) => ({
        url: `change-password/${body.id}`,
        method: "PUT",
        body: body
      }),
    }),
    clinicSignup: builder.mutation({
      query: (body) => ({
        url: `clinic-signup`,
        method: "POST",
        body: body
      }),
    }),
    teamSignup: builder.mutation({
      query: (body) => ({
        url: `team-signup`,
        method: "POST",
        body: body
      }),
    }),
    physicianProfileImage: builder.mutation({
      query: (body) => ({
        url: `physician-profile-image`,
        method: "POST",
        body: body
      }),
    }),
    physicianCoverImage: builder.mutation({
      query: (body) => ({
        url: `physician-cover-image`,
        method: "POST",
        body: body
      }),
    }),
    checkZipcode: builder.mutation({
      query: (body) => ({
        url: `check-zipcode`,
        method: "POST",
        body: body
      }),
    }),

    physicianAddress: builder.mutation({
      query: (body) => ({
        url: `physician-address`,
        method: "POST",
        body: body
      }),
    }),

    clinicAddress: builder.mutation({
      query: (body) => ({
        url: `clinic-address`,
        method: "POST",
        body: body
      }),
    }),
    clinicProfileImage: builder.mutation({
      query: (body) => ({
        url: `clinic-profile-image`,
        method: "POST",
        body: body
      }),
    }),
    clinicCoverImage: builder.mutation({
      query: (body) => ({
        url: `clinic-cover-image`,
        method: "POST",
        body: body
      }),
    }),
    physicianQualification: builder.mutation({
      query: (body) => ({
        url: `physician-qualification`,
        method: "POST",
        body: body
      }),
    }),
    clinicProfile: builder.mutation({
      query: (body) => ({
        url: `create-clinic-profile`,
        method: "POST",
        body: body
      }),
    }),
    resetPassword: builder.mutation({
      query: (body) => ({
        url: `reset-password`,
        method: "POST",
        body: body
      }),
    }),
    npiVerification: builder.mutation({
      query: (body) => ({
        url: `npi-verification`,
        method: "POST",
        body: body
      }),
    }),
    authSwitch: builder.mutation({
      query: (body) => ({
        url: `auth-switch`,
        method: "POST",
        body: body
      }),
    }),
    verifyTwoFactorOtp: builder.mutation({
      query: (body) => ({
        url: `verify-2factor`,
        method: "POST",
        body: body
      }),
    }),
    sendInvitation: builder.mutation({
      query: (body) => ({
        url: `send-invitation`,
        method: "POST",
        body: body
      }),
    }),
    getInvitation: builder.query({
      query: (body) => ({
        url: `team/${body}`,
        method: "GET",

      }),
    }),

    sendOtp: builder.mutation({
      query: (body) => ({
        url: `send-otp`,
        method: "POST",
        body: body
      }),
    }),
    addRepFavourite: builder.mutation({
      query: (body) => ({
        url: `add-rep-favourite`,
        method: "POST",
        body: body
      }),
      invalidatesTags: ['RepFavourites'],
    }),
    updateRepsFavourite: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `update-rep-favourite/${id}`,
        method: "PUT",
        body: body
      }),
      invalidatesTags: ['RepFavourites'],
    }),
    removeRepFavourite: builder.mutation({
      query: (body) => ({
        url: `remove-rep-favourite`,
        method: "DELETE",
        body: body
      }),
      invalidatesTags: ['RepFavourites'],
    }),
    favouriteList: builder.query({
      query: () => ({
        url: `rep-favourite`,
        method: "GET",
      }),
      providesTags: ['RepFavourites'],
    }),
    accountStatus: builder.mutation({
      query: (body) => ({
        url: `account-status`,
        method: "POST",
        body: body
      }),
    }),
    clinicSetting: builder.mutation({
      query: (body) => ({
        url: `account-status-clinic`,
        method: "POST",
        body: body
      }),
    }),
    addSearchHistory: builder.mutation({
      query: (body) => ({
        url: `add-search-history/`,
        method: "POST",
        body: body
      }),
      invalidatesTags: ['SearchHistory'],
    }),
    findDoctorsAccess: builder.query({
      query: () => ({ url: "find-doctors-access", method: "GET", cache: "no-store" }),
      keepUnusedDataFor: 0,
    }),
    doctorsList: builder.query({
      query: (data) => ({
        url: `doctors-list${data.userId ? '?userId=' + data.userId : ''}${data.filter ? '&sortBy=' + data.filter : ''}${data.page ? '&page=' + data.page : ''}${data.search ? '&search=' + data.search : ''}`,
        method: "GET",
      }),
    }),
    searchHistory: builder.query({
      query: (arg) => {
        const userID = typeof arg === "object" ? arg.userId : arg;
        const type = typeof arg === "object" ? arg.type : null;
        return {
          url: `search-history/${userID}${type ? `?type=${encodeURIComponent(type)}` : ""}`,
          method: "GET",
        };
      },
      providesTags: ['SearchHistory'],
    }),
    deleteSearchHistory: builder.mutation({
      query: (historyID) => ({
        url: `delete-history/${historyID}`,
        method: "DELETE",
      }),
      invalidatesTags: ['SearchHistory'],
    }),
    googleSignin: builder.mutation({
      query: (body) => ({
        url: `google-signin`,
        method: "POST",
        timeout: LOGIN_TIMEOUT_MS,
        body: body
      }),
    }),
  })
})


export const {
  useUserSignupMutation,
  usePhysicianQualificationMutation,
  usePhysicianProfileImageMutation,
  useClinicProfileImageMutation,
  useClinicCoverImageMutation,
  usePhysicianCoverImageMutation,
  useCheckZipcodeMutation,
  usePhysicianAddressMutation,
  useClinicAddressMutation,
  useClinicSignupMutation,
  useUserSigninMutation,
  useSetPasswordMutation,
  useClinicProfileMutation,
  useResetPasswordMutation,
  useSendOtpMutation,
  useAuthSwitchMutation,
  useVerifyTwoFactorOtpMutation,
  useTeamSignupMutation,
  useSendInvitationMutation,
  useGetInvitationQuery,
  useAddRepFavouriteMutation,
  useUpdateRepsFavouriteMutation,
  useRemoveRepFavouriteMutation,
  useFavouriteListQuery,
  useAccountStatusMutation,
  useClinicSettingMutation,
  useDoctorsListQuery,
  useFindDoctorsAccessQuery,
  useNpiVerificationMutation,
  useSearchHistoryQuery,
  useAddSearchHistoryMutation,
  useDeleteSearchHistoryMutation,
  useGoogleSigninMutation
} = authApi
